import { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { findProducts, type Product } from '../../../api/products';
import { toUserMessage } from '../../../api/client';
import { Button } from '../Button';
import { Dropdown } from '../Dropdown';

const ITEMS_PER_PAGE = 20;

const List = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  max-height: 240px;
  overflow-y: auto;
`;

const ListItem = styled.li<{ $selected: boolean }>`
  padding: ${({ theme }) => theme.space[1]}px ${({ theme }) => theme.space[2]}px;
  cursor: pointer;
  font-size: ${({ theme }) => theme.fontSizes[3]}px;
  background: ${({ $selected, theme }) =>
    $selected ? theme.colors.highlight.blue25 : 'transparent'};
  color: ${({ $selected, theme }) =>
    $selected ? theme.colors.primary.main : theme.colors.text.primary};
  transition: background 150ms;

  &:hover {
    background: ${({ $selected, theme }) =>
      $selected ? theme.colors.highlight.blue25 : theme.colors.highlight.gray25};
  }
`;

const EmptyMessage = styled.div`
  padding: ${({ theme }) => theme.space[2]}px;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.fontSizes[3]}px;
`;

const LoadingMessage = styled.div`
  padding: ${({ theme }) => theme.space[2]}px;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.fontSizes[3]}px;
`;

const LoadMoreSentinel = styled.div`
  height: 1px;
  visibility: hidden;
`;

export interface ProductSelectProps {
  value?: Product | null;
  onChange?: (product: Product | null) => void;
  placeholder?: string;
  disabled?: boolean;
}

export function ProductSelect({
  value,
  onChange,
  placeholder = 'Выберите изделие',
  disabled = false,
}: ProductSelectProps) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<Product[]>([]);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);

  const hasMore = items.length < totalCount;

  const loadInitial = useCallback(async () => {
    setLoading(true);
    setError(null);
    setPage(1);
    try {
      const data = await findProducts({
        page: 1,
        itemsPerPage: ITEMS_PER_PAGE,
        order: [{ field: 'name', direction: 'ASC' }],
      });
      setItems(data.items);
      setTotalCount(data.count);
    } catch (err) {
      setError(toUserMessage(err));
      setItems([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore) return;
    const nextPage = page + 1;
    setLoadingMore(true);
    try {
      const data = await findProducts({
        page: nextPage,
        itemsPerPage: ITEMS_PER_PAGE,
        order: [{ field: 'name', direction: 'ASC' }],
      });
      setItems((prev) => [...prev, ...data.items]);
      setPage(nextPage);
    } finally {
      setLoadingMore(false);
    }
  }, [page, hasMore, loadingMore]);

  useEffect(() => {
    if (open) {
      loadInitial();
    }
  }, [open, loadInitial]);

  useEffect(() => {
    if (!open || !hasMore || loading || loadingMore) return;

    const list = listRef.current;
    const sentinel = sentinelRef.current;
    if (!list || !sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry?.isIntersecting) {
          void loadMore();
        }
      },
      { root: list, rootMargin: '80px', threshold: 0 },
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [open, hasMore, loading, loadingMore, loadMore]);

  const handleSelect = useCallback(
    (product: Product) => {
      onChange?.(product);
      setOpen(false);
    },
    [onChange],
  );

  const triggerLabel = value ? value.name : placeholder;

  return (
    <Dropdown
      trigger={
        <Button variant="outlined" color="primary" disabled={disabled}>
          {triggerLabel}
        </Button>
      }
      open={open}
      onOpenChange={setOpen}
      width="280px"
    >
      {loading && <LoadingMessage>Загрузка...</LoadingMessage>}
      {error && <EmptyMessage role="alert">{error}</EmptyMessage>}
      {!loading && !error && items.length === 0 && (
        <EmptyMessage>Нет изделий</EmptyMessage>
      )}
      {!loading && !error && items.length > 0 && (
        <List ref={listRef} role="listbox">
          {items.map((product) => (
            <ListItem
              key={product.id}
              $selected={value?.id === product.id}
              role="option"
              onClick={() => handleSelect(product)}
            >
              {product.name}
              {product.category && (
                <span style={{ opacity: 0.8, fontSize: '0.9em' }}>
                  {' '}
                  — {product.category}
                </span>
              )}
            </ListItem>
          ))}
          {hasMore && <LoadMoreSentinel ref={sentinelRef} data-sentinel />}
          {loadingMore && <LoadingMessage>Загрузка…</LoadingMessage>}
        </List>
      )}
    </Dropdown>
  );
}
